/**
 * The CM desk — the conductor's view, on Home, staff only.
 *
 * Renders nothing for non-staff: /v1/cm/me 403s and every other CM endpoint
 * is gated server-side, so hiding here is cosmetic, not security. For staff it
 * shows the portfolio (active campaigns for their manager e-mail), drafts
 * awaiting first-pass with the agent's verdicts, and the outbox — messages
 * agents drafted that only become real when a human approves them. Dispatch
 * is off in v1: approving means "ready", nothing is sent.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase, FileWarning, Mail, Check, X, ChevronDown, ChevronUp, Copy, BadgeCheck,
} from 'lucide-react';
import { coworkApi, type CmDraftReview, type CmReconciliation } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';
import { InboxSection } from './InboxSection';
import { cn } from '@/components/ui/utils';

function useStaff() {
  return useQuery({
    queryKey: ['cowork', 'cm', 'me'],
    queryFn: () => coworkApi.cm.me().catch(() => ({ staff: false, email: null })),
    enabled: isCoworkLive(),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

function ReviewBadge({ r }: { r: CmDraftReview }) {
  if (r.status === 'clean') return <span className="rounded bg-[#12b76a]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#12b76a]">clean</span>;
  if (r.status === 'flagged') return <span className="rounded bg-[#f79009]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#f79009]">{r.verdict?.flags.length ?? 0} flags</span>;
  return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">{r.status}</span>;
}

/**
 * `asPage`: the desk is the whole page (its own route, the BO's "CM desk"), not a block on Home. Then it says what
 * is happening instead of rendering nothing while it loads or for someone outside the pilot.
 */
export function CMDesk({ asPage = false }: { asPage?: boolean } = {}) {
  const { data: me, isPending: mePending } = useStaff();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [openReview, setOpenReview] = useState<string | null>(null);
  const [showOutbox, setShowOutbox] = useState(true);
  const [reconTarget, setReconTarget] = useState<string | null>(null);

  const staff = me?.staff === true;
  const { data: nm } = useQuery({
    queryKey: ['cowork', 'cm', 'needs-me'],
    queryFn: () => coworkApi.cm.needsMe(),
    enabled: staff,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const { data: outbox } = useQuery({
    queryKey: ['cowork', 'cm', 'outbox'],
    queryFn: () => coworkApi.cm.outbox('draft'),
    enabled: staff,
    refetchInterval: 120_000,
  });

  if (!staff || !nm) {
    if (!asPage) return null;
    const note = mePending || (staff && !nm)
      ? 'Loading the desk…'
      : 'The CM desk is open to campaign managers in the agents pilot. Ask an admin to add your email.';
    return <p className="py-16 text-center text-[13px] text-[#98a2b3]">{note}</p>;
  }

  const reviews = Object.values(nm.reviewsByCampaign ?? {}).flat();
  const flagged = reviews.filter(r => r.status === 'flagged');
  const drafts = outbox?.messages ?? [];

  async function resolve(id: string, action: 'approve' | 'dismiss') {
    await coworkApi.cm.outboxResolve(id, action);
    void qc.invalidateQueries({ queryKey: ['cowork', 'cm'] });
  }

  return (
    <section aria-label="CM desk" className="rounded-2xl border border-[#2a5fb0]/20 bg-[#2a5fb0]/[0.03] p-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <BadgeCheck className="h-4 w-4 self-center text-[#2a5fb0]" aria-hidden />
        <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>CM desk</h2>
        <span className="text-xs text-[#667085]">
          portfolio of {nm.manager ?? '—'} · {nm.campaigns.length} active campaign{nm.campaigns.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Summary tiles */}
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <button type="button" onClick={() => navigate('/campaigns')} className="rounded-xl bg-white px-3 py-2 text-left shadow-sm transition-shadow hover:shadow-md">
          <p className="text-[11px] text-[#98a2b3]"><Briefcase className="mr-1 inline h-3 w-3" aria-hidden />Campaigns</p>
          <p className="text-lg tabular-nums text-[#101828]" style={{ fontWeight: 700 }}>{nm.campaigns.length}</p>
        </button>
        <button type="button"
          onClick={() => nm.contents.cmReview[0] && navigate(`/campaigns/${nm.contents.cmReview[0].campaign_id}/content-approval`)}
          className="rounded-xl bg-white px-3 py-2 text-left shadow-sm transition-shadow hover:shadow-md">
          <p className="text-[11px] text-[#98a2b3]"><FileWarning className="mr-1 inline h-3 w-3" aria-hidden />Drafts to review</p>
          <p className="text-lg tabular-nums text-[#101828]" style={{ fontWeight: 700 }}>
            {nm.contents.cmReview.length}
            {flagged.length > 0 && <span className="ml-1 text-xs text-[#f79009]">({flagged.length} pre-flagged)</span>}
          </p>
        </button>
        <button type="button"
          onClick={() => nm.metrics[0] ? navigate(`/campaigns/${nm.metrics[0].campaign_id}/results`) : navigate('/campaigns')}
          className="rounded-xl bg-white px-3 py-2 text-left shadow-sm transition-shadow hover:shadow-md">
          <p className="text-[11px] text-[#98a2b3]">Missing metrics</p>
          <p className="text-lg tabular-nums text-[#101828]" style={{ fontWeight: 700 }}>{nm.metrics.length}</p>
        </button>
        <button type="button" onClick={() => navigate('/messages')} className="rounded-xl bg-white px-3 py-2 text-left shadow-sm transition-shadow hover:shadow-md">
          <p className="text-[11px] text-[#98a2b3]"><Mail className="mr-1 inline h-3 w-3" aria-hidden />Outbox drafts</p>
          <p className="text-lg tabular-nums text-[#101828]" style={{ fontWeight: 700 }}>{drafts.length}</p>
        </button>
      </div>

      {/* Draft reviews */}
      {nm.contents.cmReview.length > 0 && (
        <div className="mb-3">
          <h3 className="mb-1.5 text-[12px] uppercase tracking-wide text-[#667085]" style={{ fontWeight: 600 }}>
            Drafts awaiting your verdict: first pass done by agents
          </h3>
          <div className="space-y-1.5">
            {nm.contents.cmReview.slice(0, 8).map(row => {
              const review = (nm.reviewsByCampaign?.[row.campaign_id] ?? [])
                .find(r => r.productId === row.product_id);
              const open = openReview === row.product_id;
              return (
                <div key={row.product_id} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setOpenReview(open ? null : row.product_id)}
                    className="flex w-full items-center gap-2 text-left"
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
                      {row.creator_handle ?? 'creator'} · {row.campaign_name ?? row.campaign_id}
                      {row.beat_name && <span className="text-[#98a2b3]"> · {row.beat_name}</span>}
                    </span>
                    {review ? <ReviewBadge r={review} /> :
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">reviewing…</span>}
                    {open ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" aria-hidden />}
                  </button>
                  {open && review?.verdict && (
                    <div className="mt-2 border-t border-gray-100 pt-2 text-[12px] text-[#475467]">
                      <button type="button" onClick={() => navigate(`/campaigns/${row.campaign_id}/content-approval`)}
                        className="mb-1.5 text-[12px] text-[#be43ff] hover:underline" style={{ fontWeight: 600 }}>
                        Open campaign content →
                      </button>
                      <p className="mb-1 text-[11px] italic text-[#98a2b3]">Reviewed: {review.verdict.reviewed}</p>
                      {review.verdict.flags.length === 0
                        ? <p className="text-[#12b76a]">No issues found: your call.</p>
                        : <ul className="list-disc space-y-0.5 pl-4">{review.verdict.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>}
                      {review.verdict.suggestedFeedback && (
                        <div className="mt-2 rounded-lg bg-[#f8f9fa] p-2">
                          <p className="mb-1 text-[10px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>Suggested feedback (copy → paste in review)</p>
                          <p className="whitespace-pre-wrap">{review.verdict.suggestedFeedback}</p>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(review.verdict!.suggestedFeedback!)}
                            className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-[#475467] hover:border-gray-300"
                          >
                            <Copy className="h-3 w-3" aria-hidden /> Copy
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Outbox */}
      {drafts.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowOutbox(v => !v)}
            className="mb-1.5 flex items-center gap-1 text-[12px] uppercase tracking-wide text-[#667085]"
            style={{ fontWeight: 600 }}
          >
            Outbox: approve or dismiss ({drafts.length})
            {showOutbox ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
          </button>
          {showOutbox && (
            <div className="space-y-1.5">
              {drafts.slice(0, 6).map(m => (
                <div key={m.id} className="rounded-xl bg-white px-3 py-2 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      m.purpose.startsWith('chase') ? 'bg-[#f79009]/10 text-[#f79009]' : 'bg-[#2a5fb0]/10 text-[#2a5fb0]')}>
                      {m.purpose}
                    </span>
                    <button type="button"
                      onClick={() => m.campaignId.startsWith('chatwoot:')
                        ? navigate(`/messages?c=${m.campaignId.slice(9)}`)
                        : navigate(`/campaigns/${m.campaignId}`)}
                      className="min-w-0 flex-1 truncate text-left text-[13px] text-[#101828] hover:text-[#be43ff]" style={{ fontWeight: 600 }}>
                      {m.influencerHandle} · {m.subject}
                    </button>
                    <button type="button" onClick={() => void resolve(m.id, 'approve')}
                      title="Approve (dispatch is off: nothing sends in v1)"
                      className="rounded-md bg-[#12b76a]/10 p-1.5 text-[#12b76a] hover:bg-[#12b76a]/20">
                      <Check className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button type="button" onClick={() => void resolve(m.id, 'dismiss')}
                      title="Dismiss"
                      className="rounded-md bg-gray-100 p-1.5 text-gray-500 hover:bg-gray-200">
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-[12px] text-[#667085]">{m.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <InboxSection />

      {/* Close-out: pick a campaign, get the reconciliation picture. */}
      <div className="mt-3">
        <h3 className="mb-1.5 text-[12px] uppercase tracking-wide text-[#667085]" style={{ fontWeight: 600 }}>
          Close-out check
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {nm.campaigns.slice(0, 8).map(c => (
            <button
              key={c.campaign_id}
              type="button"
              onClick={() => setReconTarget(reconTarget === c.campaign_id ? null : c.campaign_id)}
              className={cn(
                'rounded-lg border px-2.5 py-1 text-[12px] transition-colors',
                reconTarget === c.campaign_id
                  ? 'border-[#2a5fb0] bg-[#2a5fb0]/10 text-[#2a5fb0]'
                  : 'border-gray-200 bg-white text-[#475467] hover:border-gray-300',
              )}
              style={{ fontWeight: 600 }}
            >
              {c.name.slice(0, 26)}{c.name.length > 26 ? '…' : ''}
            </button>
          ))}
        </div>
        {reconTarget && <ReconPanel campaignId={reconTarget} />}
      </div>

      <p className="mt-2 text-[11px] text-[#98a2b3]">
        Approving a message marks it ready: dispatch is off in v1, nothing is sent. Draft verdicts are advisory; the review decision stays yours.
      </p>
    </section>
  );
}

function ReconPanel({ campaignId }: { campaignId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<CmReconciliation>({
    queryKey: ['cowork', 'cm', 'reconcile', campaignId],
    queryFn: () => coworkApi.cm.reconcile(campaignId),
    staleTime: 5 * 60_000,
  });
  if (isLoading) return <p className="mt-2 text-[12px] text-[#98a2b3]">Reading the campaign…</p>;
  if (!data) return null;
  return (
    <div className="mt-2 rounded-xl bg-white p-3 shadow-sm">
      <p className="mb-1.5 text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
        <button type="button" onClick={() => navigate(`/campaigns/${campaignId}`)} className="hover:text-[#be43ff] hover:underline">
          {data.campaignName}
        </button> · {data.status}
        <span className={cn('ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold',
          data.readyToClose ? 'bg-[#12b76a]/10 text-[#12b76a]' : 'bg-[#f79009]/10 text-[#f79009]')}>
          {data.readyToClose ? 'ready to close' : 'not ready to close'}
        </span>
      </p>
      <ul className="space-y-0.5 text-[12px]">
        {data.checklist.map(i => (
          <li key={i.id} className="flex gap-1.5">
            <span className={i.pass ? 'text-[#12b76a]' : 'text-[#f79009]'}>{i.pass ? '✓' : '✗'}</span>
            <span className="text-[#475467]"><b className="text-[#101828]">{i.label}.</b> {i.detail}</span>
          </li>
        ))}
      </ul>
      {data.diffs.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-1.5 text-[12px] text-[#475467]">
          {data.diffs.map((d, i) => (
            <p key={i}><b className="text-[#101828]">{d.kind}:</b> planned {d.planned} → {d.actual}. {d.note}</p>
          ))}
        </div>
      )}
      <p className="mt-1.5 text-[11px] text-[#98a2b3]">{data.note}</p>
    </div>
  );
}
